import EventEmitter from 'events';

import s from './strings';

const RULE_ORDER = [
  'required', 'internal', 'email', 'rangelength', 'digits', 'validateFunction',
];

export class FormController extends EventEmitter {
  constructor(fields) {
    super();

    this._fields = fields;
    this._fieldsByName = new Map();
    this._state = {};
    this.errors = [];

    this._fields.forEach(field => {
      this._fieldsByName.set(field.name, field);
      this._state[field.name] = null;

      Object.keys(field.rules).forEach(ruleType => {
        if (!RULE_ORDER.includes(ruleType)) {
          if (!['ignore', 'errorMessage', 'noCheckInteractive'].includes(ruleType)) {
            throw new TypeError(`${field.name}: unknown rule option ${ruleType}`);
          }
        }
      });
    });
  }

  submit(form) {
    this._state = {};
    this.errors = [];

    for (const field of this._fields) {
      const fieldName = field.name;
      const errorType = this.checkField(form, fieldName);
      if (errorType === true) {
        this._state[fieldName] = 'valid';
      } else {
        this._state[fieldName] = errorType;
        this.errors.push({
          fieldName,
          errorType,
          message: this.getFieldErrorMessage(field, errorType),
        });
      }
    }

    this.emit('update');

    if (!this.errors.length) {
      this.emit('submit', form.getAllValues());
    }
  }

  checkField(form, fieldName) {
    const field = this._fieldsByName.get(fieldName);
    const rules = field.rules;

    if (rules.ignore && rules.ignore()) {
      return true;
    }

    const value = form.get(fieldName);

    for (let ruleType of RULE_ORDER) {
      const isInternal = ruleType === 'internal';

      if (!(ruleType in rules) && !isInternal) {
        continue;
      }

      const ruleParams = rules[ruleType];
      const result = this[`test_${ruleType}`](ruleParams, value, fieldName, form);

      if (typeof result === 'string') {
        return result;
      }

      if (!result) {
        return ruleType;
      }
    }

    return true;
  }

  checkInteractive(form, fieldName, eventType) {
    const errorType = this.checkField(form, fieldName);

    if (errorType === true) {
      this._state[fieldName] = 'valid';
    } else if (eventType === 'blur') {
      this._state[fieldName] = errorType;
    } else {
      this._state[fieldName] = null;
    }

    this.emit('update');
  }

  setErrors(errors) {
    this.errors = errors.map(({ fieldName, errorType }) => {
      const field = this._fieldsByName.get(fieldName);

      this._state[fieldName] = errorType;

      return {
        fieldName,
        errorType,
        message: this.getFieldErrorMessage(field, errorType),
      };
    });

    this.emit('update');
  }

  validateLoadedFields(form) {
    this.errors = [];

    for (const field of this._fields) {
      const fieldName = field.name;
      const errorType = this.checkField(form, fieldName);

      if (errorType === true) {
        this._state[fieldName] = 'valid';
      } else if (errorType === 'required') {
        this._state[field.name] = null;
      } else {
        this._state[fieldName] = errorType;
      }
    }

    this.emit('update');
  }

  getFieldErrorMessage(field, errorType) {
    const { label } = field;
    const { errorMessage } = field.rules;

    if (errorMessage) {
      return errorMessage;
    }

    const errorTypeKey = errorType === 'required'
      ? 'required'
      : 'invalid';
    const prefix = s.ValidatedForm[`${errorTypeKey}_prefix`];
    const suffix = s.ValidatedForm[`${errorTypeKey}_suffix`];
    return `${prefix}${label}${suffix}`;
  }

  get fields() {
    return this._fields;
  }

  get state() {
    return this._state;
  }

  // Rule test methods

  test_required(params, value) {
    const optional = typeof params === 'function'
      ? !params()
      : !params;

    if (optional) {
      return true;
    }

    return !!(typeof value == 'string' ? value.length : value);
  }

  test_internal(params, value, fieldName, form) {
    if (!form.check(fieldName)) {
      return 'invalid';
    }

    return true;
  }

  test_email(params, value) {
    if (!value) {
      return true;
    }
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(value);
  }

  test_rangelength([ min, max ], value) {
    return (value.length >= min && value.length <= max);
  }

  test_digits(params, value) {
    return /^\d+$/.test(value);
  }

  test_validateFunction(func, value) {
    return func(value);
  }
}
